import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Pencil, Play, Archive, ShieldOff, CheckCircle2, Plus, Loader2, Trash2, Eye } from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { StatusBadge } from "@/components/StatusBadge";
import { LoadingBlock } from "@/components/Pagination";
import { api, formatApiError } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { AddVideoDialog } from "@/components/admin/AddVideoDialog";
import { AdminVideoPreviewDialog } from "@/components/admin/AdminVideoPreviewDialog";
import { AddDocumentDialog } from "@/components/admin/AddDocumentDialog";
import { AdminDocumentPreviewDialog } from "@/components/admin/AdminDocumentPreviewDialog";
import { AdminQuizLinkDialog } from "@/components/admin/AdminQuizLinkDialog";
import { EditLessonDialog } from "@/components/admin/EditLessonDialog";
import { useAuth } from "@/context/AuthContext";

const LESSON_TYPES = ["video", "pdf", "ppt", "text", "quiz", "assignment", "live_class"];

export default function AdminCourseDetailPage() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSuperAdmin = user?.role_code === "super_admin";
  const [tab, setTab] = useState("overview");
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);

  const [modules, setModules] = useState([]);
  const [modulesLoading, setModulesLoading] = useState(true);
  const [modDlg, setModDlg] = useState(null); // {create:true} or module object
  const [modForm, setModForm] = useState({ module_title: "", description: "", estimated_duration_minutes: 30, display_order: 0 });

  const [lessonDlg, setLessonDlg] = useState(null); // {module_id}
  const [lessonForm, setLessonForm] = useState({ module_id: "", lesson_title: "", lesson_type: "video", estimated_duration_minutes: 0, display_order: 0 });

  const [statusDlg, setStatusDlg] = useState(null); // { targetStatus, needsReason }
  const [statusReason, setStatusReason] = useState("");

  const [videoLessonId, setVideoLessonId] = useState(null);   // Add Video dialog target lesson
  const [previewLesson, setPreviewLesson] = useState(null);   // View / preview dialog
  const [editLesson, setEditLesson] = useState(null);         // Super Admin — edit lesson dialog
  const [docLesson, setDocLesson] = useState(null);           // Add-Document dialog target
  const [previewDocLesson, setPreviewDocLesson] = useState(null); // View-document dialog
  const [quizLinkLesson, setQuizLinkLesson] = useState(null); // Link/preview quiz dialog target
  const [dragging, setDragging] = useState(null);   // { moduleId, lessonId } during a drag
  const [videos, setVideos] = useState([]);
  const [videosLoading, setVideosLoading] = useState(false);
  const [reprocessing, setReprocessing] = useState({});


  // Phase L1 — course ↔ exam links
  const [examLinks, setExamLinks] = useState([]);
  const [availableExams, setAvailableExams] = useState([]);
  const [linkDlg, setLinkDlg] = useState(null); // {create:true} | link object
  const [linkForm, setLinkForm] = useState({
    exam_id: "", module_id: "", display_title: "", exam_type: "post_test",
    display_order: 100, is_mandatory: true,
    required_for_course_completion: true, required_for_certificate: true,
    unlock_condition: "after_all_required_lessons_completion",
    minimum_completion_percentage: 100, status: "published",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/courses/${courseId}`);
      setPayload(data.data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, [courseId]);
  useEffect(() => { load(); }, [load]);

  const loadModules = useCallback(async () => {
    setModulesLoading(true);
    try {
      const { data } = await api.get(`/admin/courses/${courseId}/modules`);
      const list = data.data.modules;
      for (const m of list) {
        const { data: ld } = await api.get(`/admin/courses/${courseId}/lessons`, { params: { module_id: m.id } });
        m.lessons = ld.data.lessons;
      }
      setModules(list);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setModulesLoading(false); }
  }, [courseId]);
  useEffect(() => { if (tab === "curriculum") loadModules(); }, [tab, loadModules]);

  const loadExamLinks = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/courses/${courseId}/exam-links`);
      setExamLinks(data.data.exam_links || []);
    } catch { /* silent */ }
  }, [courseId]);
  const loadAvailableExams = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/courses/${courseId}/available-exams`);
      setAvailableExams(data.data.exams || []);
    } catch { /* silent */ }
  }, [courseId]);
  useEffect(() => { if (tab === "curriculum") { loadExamLinks(); loadAvailableExams(); } }, [tab, loadExamLinks, loadAvailableExams]);

  const loadVideos = useCallback(async () => {
    setVideosLoading(true);
    try {
      const { data } = await api.get(`/admin/courses/${courseId}/videos`);
      setVideos(data.data?.videos || []);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setVideosLoading(false); }
  }, [courseId]);
  useEffect(() => { if (tab === "videos") loadVideos(); }, [tab, loadVideos]);
  useEffect(() => {
    if (tab !== "videos") return undefined;
    const busy = videos.some((v) => ["processing", "queued", "uploading", "uploaded"].includes(v.compression_status)
                                  || ["uploading", "uploaded"].includes(v.upload_status));
    if (!busy) return undefined;
    const t = setInterval(loadVideos, 5000);
    return () => clearInterval(t);
  }, [tab, videos, loadVideos]);

  const reprocessVideo = async (videoId) => {
    setReprocessing((s) => ({ ...s, [videoId]: true }));
    try {
      await api.post(`/admin/lesson-videos/${videoId}/retry-processing`);
      toast.success("Re-processing started. This may take a minute.");
      setTimeout(loadVideos, 800);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setReprocessing((s) => { const n = { ...s }; delete n[videoId]; return n; }); }
  };

  const deleteVideo = async (v) => {
    if (!window.confirm(`Delete video "${v.video_title}"? This soft-deletes it and hides it from learners.`)) return;
    try {
      await api.delete(`/admin/lesson-videos/${v.id}`);
      toast.success("Video removed.");
      loadVideos();
    } catch (err) { toast.error(formatApiError(err)); }
  };


  const submitExamLink = async () => {
    if (!linkForm.exam_id) return toast.error("Select an exam to link.");
    try {
      if (linkDlg?.create) {
        await api.post(`/admin/courses/${courseId}/exam-links`, linkForm);
        toast.success("Exam linked to course.");
      } else {
        // Only send changed / relevant fields for PUT
        const { exam_id, ...rest } = linkForm;
        void exam_id;
        await api.put(`/admin/course-exam-links/${linkDlg.id}`, rest);
        toast.success("Exam link updated.");
      }
      setLinkDlg(null);
      setLinkForm({
        exam_id: "", module_id: "", display_title: "", exam_type: "post_test",
        display_order: 100, is_mandatory: true,
        required_for_course_completion: true, required_for_certificate: true,
        unlock_condition: "after_all_required_lessons_completion",
        minimum_completion_percentage: 100, status: "published",
      });
      loadExamLinks(); loadAvailableExams();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const deleteExamLink = async (linkId) => {
    if (!window.confirm("Remove this exam from the course curriculum?")) return;
    try {
      await api.delete(`/admin/course-exam-links/${linkId}`);
      toast.success("Exam link removed.");
      loadExamLinks(); loadAvailableExams();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const toggleLinkStatus = async (linkId, newStatus) => {
    try {
      await api.patch(`/admin/course-exam-links/${linkId}/status`, { status: newStatus });
      toast.success(`Status changed to ${newStatus}.`);
      loadExamLinks();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const submitStatus = async () => {
    try {
      const body = { status: statusDlg.targetStatus };
      if (["archived", "suspended"].includes(statusDlg.targetStatus)) {
        if (statusReason.trim().length < 3) return toast.error("Reason must be at least 3 characters.");
        body.reason = statusReason;
      }
      await api.patch(`/admin/courses/${courseId}/status`, body);
      toast.success(`Course ${statusDlg.targetStatus}.`);
      setStatusDlg(null); setStatusReason(""); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const submitModule = async () => {
    try {
      if (modDlg?.create) {
        await api.post(`/admin/courses/${courseId}/modules`, modForm);
        toast.success("Module added.");
      } else {
        const body = { ...modForm };
        await api.put(`/admin/courses/${courseId}/modules/${modDlg.id}`, body);
        toast.success("Module updated.");
      }
      setModDlg(null); setModForm({ module_title: "", description: "", estimated_duration_minutes: 30, display_order: 0 });
      loadModules();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const submitLesson = async () => {
    try {
      await api.post(`/admin/courses/${courseId}/lessons`, { ...lessonForm, module_id: lessonDlg.module_id });
      toast.success("Lesson added.");
      setLessonDlg(null); setLessonForm({ module_id: "", lesson_title: "", lesson_type: "video", estimated_duration_minutes: 0, display_order: 0 });
      loadModules();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const removeLesson = async (l) => {
    if (!window.confirm(`Remove lesson "${l.lesson_title}"? This cannot be undone from the UI.`)) return;
    try {
      await api.delete(`/admin/courses/${courseId}/lessons/${l.id}`);
      toast.success("Lesson removed.");
      loadModules();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const removeModule = async (m) => {
    const n = m.lessons?.length || 0;
    if (!window.confirm(`Remove module "${m.module_title}"${n ? ` and its ${n} lesson${n === 1 ? "" : "s"}` : ""}? This cannot be undone from the UI.`)) return;
    try {
      await api.delete(`/admin/courses/${courseId}/modules/${m.id}`);
      toast.success("Module removed.");
      loadModules();
    } catch (err) { toast.error(formatApiError(err)); }
  };


  // -------- Drag-and-drop lesson reorder (native HTML5 DnD) --------
  // Supports two flows:
  //   1) Reorder within the same module (drop on another lesson) — uses the
  //      per-module reorder endpoint.
  //   2) Move a lesson into a different module (drop on a lesson in that
  //      module OR onto an empty module drop zone) — uses the cross-module
  //      endpoint which normalises module_id + display_order in one shot.
  const onLessonDragStart = (e, moduleId, lessonId) => {
    setDragging({ moduleId, lessonId });
    try { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", lessonId); } catch { /* ignore */ }
  };
  const onLessonDragOver = (e) => { e.preventDefault(); try { e.dataTransfer.dropEffect = "move"; } catch { /* ignore */ } };

  const persistCrossModule = async (nextModules) => {
    // Optimistic UI update first
    setModules(nextModules);
    try {
      await api.post(`/admin/courses/${courseId}/reorder-lessons-cross-module`, {
        modules: nextModules.map((m) => ({
          module_id: m.id,
          lesson_ids: (m.lessons || []).map((l) => l.id),
        })),
      });
      toast.success("Curriculum saved.");
    } catch (err) { toast.error(formatApiError(err)); loadModules(); }
  };

  const onLessonDrop = async (e, targetModuleId, targetLessonId) => {
    e.preventDefault();
    e.stopPropagation();
    const src = dragging;
    setDragging(null);
    if (!src) return;
    if (src.lessonId === targetLessonId && src.moduleId === targetModuleId) return;

    // Same-module reorder — cheap per-module endpoint
    if (src.moduleId === targetModuleId) {
      const mod = modules.find((m) => m.id === targetModuleId);
      if (!mod?.lessons) return;
      const ids = mod.lessons.map((l) => l.id);
      const from = ids.indexOf(src.lessonId);
      const to = ids.indexOf(targetLessonId);
      if (from < 0 || to < 0) return;
      ids.splice(to, 0, ids.splice(from, 1)[0]);
      setModules((ms) => ms.map((m) => m.id === targetModuleId
        ? { ...m, lessons: ids.map((id) => m.lessons.find((l) => l.id === id)) }
        : m));
      try {
        await api.post(`/admin/courses/${courseId}/modules/${targetModuleId}/reorder-lessons`, { lesson_ids: ids });
        toast.success("Lesson order saved.");
      } catch (err) { toast.error(formatApiError(err)); loadModules(); }
      return;
    }

    // Cross-module move — build the full new layout and call the atomic endpoint
    const srcMod = modules.find((m) => m.id === src.moduleId);
    const tgtMod = modules.find((m) => m.id === targetModuleId);
    if (!srcMod || !tgtMod) return;
    const moving = (srcMod.lessons || []).find((l) => l.id === src.lessonId);
    if (!moving) return;
    const nextModules = modules.map((m) => {
      if (m.id === srcMod.id) {
        return { ...m, lessons: (m.lessons || []).filter((l) => l.id !== src.lessonId) };
      }
      if (m.id === tgtMod.id) {
        const lessons = [...(m.lessons || [])];
        const insertAt = targetLessonId
          ? Math.max(0, lessons.findIndex((l) => l.id === targetLessonId))
          : lessons.length;
        lessons.splice(insertAt, 0, { ...moving, module_id: tgtMod.id });
        return { ...m, lessons };
      }
      return m;
    });
    await persistCrossModule(nextModules);
  };

  // Drop onto an empty module area (e.g. module without any lessons) — append
  // the dragged lesson to the end of that module.
  const onModuleDrop = async (e, targetModuleId) => {
    e.preventDefault();
    e.stopPropagation();
    const src = dragging;
    setDragging(null);
    if (!src || src.moduleId === targetModuleId) return;
    const srcMod = modules.find((m) => m.id === src.moduleId);
    const tgtMod = modules.find((m) => m.id === targetModuleId);
    if (!srcMod || !tgtMod) return;
    const moving = (srcMod.lessons || []).find((l) => l.id === src.lessonId);
    if (!moving) return;
    const nextModules = modules.map((m) => {
      if (m.id === srcMod.id) {
        return { ...m, lessons: (m.lessons || []).filter((l) => l.id !== src.lessonId) };
      }
      if (m.id === tgtMod.id) {
        return { ...m, lessons: [...(m.lessons || []), { ...moving, module_id: tgtMod.id }] };
      }
      return m;
    });
    await persistCrossModule(nextModules);
  };


  if (loading) return <DashboardLayout title="Course"><LoadingBlock label="Loading course…" /></DashboardLayout>;
  if (!payload) return null;
  const c = payload.course;

  return (
    <DashboardLayout title={c.course_title} breadcrumbs={["Platform", "Courses", c.course_title]}>
      <div className="space-y-6" data-testid="admin-course-detail">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Link to="/app/super-admin/courses" className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-[#101876]">
            <ArrowLeft className="h-3.5 w-3.5" /> Back
          </Link>
          <div className="flex flex-wrap items-center gap-2">
            <Link to={`/app/super-admin/courses/${courseId}/edit`} className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-[#101876]/40 hover:text-[#101876]">
              <Pencil className="h-3.5 w-3.5" /> Edit
            </Link>
            {c.status === "draft" && (
              <button onClick={() => setStatusDlg({ targetStatus: "published" })} className="inline-flex items-center gap-1.5 rounded-md bg-[#329112] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#256a0d]" data-testid="admin-course-publish">
                <Play className="h-3.5 w-3.5" /> Publish
              </button>
            )}
            {c.status !== "archived" && (
              <button onClick={() => setStatusDlg({ targetStatus: "archived", needsReason: true })} className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">
                <Archive className="h-3.5 w-3.5" /> Archive
              </button>
            )}
            {c.status !== "suspended" && c.status !== "draft" && (
              <button onClick={() => setStatusDlg({ targetStatus: "suspended", needsReason: true })} className="inline-flex items-center gap-1.5 rounded-md border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-100">
                <ShieldOff className="h-3.5 w-3.5" /> Suspend
              </button>
            )}
            {c.status === "archived" && (
              <button onClick={() => setStatusDlg({ targetStatus: "published" })} className="inline-flex items-center gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100">
                <CheckCircle2 className="h-3.5 w-3.5" /> Restore
              </button>
            )}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="text-xs font-bold uppercase tracking-[0.15em] text-[#329112]">{c.category_name}</div>
              <div className="mt-1 text-2xl font-black text-slate-900">{c.course_title}</div>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                <span className="font-mono text-slate-600">{c.course_code}</span>
                <span>·</span>
                <span className="capitalize">{c.course_type?.replace(/_/g, " ")}</span>
                <span>·</span>
                <span className="capitalize">{c.course_level}</span>
                <span>·</span>
                <span>{c.language}</span>
                <span>·</span>
                <span>{c.estimated_duration_hours}h</span>
                {c.mandatory_eligible && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-700">Mandatory-eligible</span>}
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-600">v{c.current_version}</span>
              </div>
            </div>
            <StatusBadge status={c.status} />
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="overview" data-testid="admin-course-tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="curriculum" data-testid="admin-course-tab-curriculum">Curriculum</TabsTrigger>
            <TabsTrigger value="videos" data-testid="admin-course-tab-videos">Video Processing</TabsTrigger>
            <TabsTrigger value="mapping">Mappings</TabsTrigger>
            <TabsTrigger value="history">Status history</TabsTrigger>
            <TabsTrigger value="versions">Versions</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-4">
              <StatCard title="Modules" value={c.modules_count || 0} />
              <StatCard title="Lessons" value={c.lessons_count || 0} />
              <StatCard title="Mapped orgs" value={c.mapped_orgs_count || 0} />
              <StatCard title="Total assignments" value={c.assignments_count || 0} />
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-3">
              {c.short_description && <ParaBlock title="Short description" text={c.short_description} />}
              {c.detailed_description && <ParaBlock title="Detailed description" text={c.detailed_description} />}
              {c.course_objective && <ParaBlock title="Objective" text={c.course_objective} />}
              {c.learning_outcomes && <ParaBlock title="Learning outcomes" text={c.learning_outcomes} />}
              {c.target_audience && <ParaBlock title="Target audience" text={c.target_audience} />}
              {c.prerequisites && <ParaBlock title="Prerequisites" text={c.prerequisites} />}
              {(c.applicable_standards || []).length > 0 && (
                <div>
                  <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Applicable standards</div>
                  <div className="mt-1 flex flex-wrap gap-2">
                    {c.applicable_standards.map((s) => <span key={s} className="rounded-full bg-[#101876]/10 px-3 py-1 text-xs font-semibold text-[#101876]">{s}</span>)}
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="curriculum">
            <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <div className="text-sm font-bold text-slate-900">Modules & lessons</div>
                  <div className="text-xs text-slate-500">Video (device / Google Drive / OneDrive / URL), PDF, live class — Add Video is enabled below on every video lesson.</div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                <button onClick={() => { setModForm({ module_title: "", description: "", estimated_duration_minutes: 30, display_order: (modules.length || 0) + 1 }); setModDlg({ create: true }); }} className="inline-flex items-center gap-1.5 rounded-md bg-[#101876] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#0c135c]" data-testid="admin-module-add">
                  <Plus className="h-3.5 w-3.5" /> Add module
                </button>
                <button
                  onClick={() => {
                    setLinkForm((p) => ({
                      ...p,
                      exam_id: "", module_id: "", display_title: "",
                      display_order: (examLinks.length + modules.length + 1) * 10,
                    }));
                    setLinkDlg({ create: true });
                  }}
                  className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-[#101876]/40 hover:text-[#101876]"
                  data-testid="admin-exam-link-add"
                >
                  <Plus className="h-3.5 w-3.5" /> Add Exam / Post-Test
                </button>
                </div>
              </div>
              {modulesLoading ? <LoadingBlock label="Loading curriculum…" /> : modules.length === 0 ? (
                <div className="rounded-md border border-dashed border-slate-300 p-8 text-center text-xs text-slate-500">No modules yet. Add your first module.</div>
              ) : modules.map((m) => {
                const isDropTarget = dragging && dragging.moduleId !== m.id;
                return (
                <div
                  key={m.id}
                  onDragOver={isDropTarget ? onLessonDragOver : undefined}
                  onDrop={isDropTarget ? (e) => onModuleDrop(e, m.id) : undefined}
                  className={`rounded-md border ${isDropTarget ? "border-[#329112] bg-emerald-50/40 ring-2 ring-[#329112]/30" : "border-slate-200 bg-slate-50/50"} p-4 transition-colors`}
                  data-testid={`module-block-${m.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-bold text-slate-900">{m.display_order}. {m.module_title}</div>
                      <div className="text-xs text-slate-500">{m.estimated_duration_minutes} min · {m.lessons?.length || 0} lessons</div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => { setLessonForm({ module_id: m.id, lesson_title: "", lesson_type: "video", estimated_duration_minutes: 10, display_order: (m.lessons?.length || 0) + 1 }); setLessonDlg({ module_id: m.id }); }} className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-semibold text-slate-600 hover:border-[#101876]/40">
                        <Plus className="h-3 w-3" /> Lesson
                      </button>
                      <button onClick={() => { setModForm({ module_title: m.module_title, description: m.description || "", estimated_duration_minutes: m.estimated_duration_minutes, display_order: m.display_order }); setModDlg(m); }} className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-semibold text-slate-600 hover:border-[#101876]/40">Edit</button>
                      <button onClick={() => removeModule(m)} data-testid={`module-remove-${m.id}`} className="inline-flex items-center gap-1 rounded-md border border-rose-200 bg-white px-2 py-1 text-xs font-semibold text-rose-600 hover:bg-rose-50" title="Remove module">
                        <Trash2 className="h-3 w-3" /> Remove
                      </button>
                    </div>
                  </div>
                  {m.lessons?.length > 0 ? (
                    <div className="mt-3 space-y-1">
                      {m.lessons.map((l) => (
                        <div
                          key={l.id}
                          draggable
                          onDragStart={(e) => onLessonDragStart(e, m.id, l.id)}
                          onDragOver={onLessonDragOver}
                          onDrop={(e) => onLessonDrop(e, m.id, l.id)}
                          data-testid={`lesson-row-${l.id}`}
                          className={`flex cursor-move items-center justify-between rounded border ${dragging?.lessonId === l.id ? "border-[#101876]/60 opacity-60" : "border-slate-100"} bg-white px-3 py-2 text-xs hover:border-[#101876]/20`}
                          title="Drag to reorder within this module or across modules"
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-slate-400 font-mono">{l.display_order}.</span>
                            <span className="font-semibold text-slate-800">{l.lesson_title}</span>
                            <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[9px] font-bold uppercase text-slate-600">{l.lesson_type}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {l.lesson_type === "video" && (
                              <>
                                <button
                                  onClick={() => setPreviewLesson(l)}
                                  data-testid={`lesson-view-${l.id}`}
                                  title="Preview uploaded video"
                                  className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-[10px] font-semibold text-slate-700 hover:border-[#101876]/40 hover:text-[#101876]"
                                >
                                  <Eye className="h-3 w-3" /> View
                                </button>
                                <button
                                  onClick={() => setVideoLessonId(l.id)}
                                  data-testid={`lesson-add-video-${l.id}`}
                                  className="inline-flex items-center gap-1 rounded-md bg-[#101876] px-2 py-1 text-[10px] font-semibold text-white hover:bg-[#0c135c]"
                                >
                                  <Plus className="h-3 w-3" /> Add Video
                                </button>
                              </>
                            )}
                            {l.lesson_type !== "video" && l.lesson_type !== "quiz" && (
                              <>
                                <button
                                  onClick={() => setPreviewDocLesson(l)}
                                  data-testid={`lesson-view-doc-${l.id}`}
                                  title="Preview document"
                                  className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-[10px] font-semibold text-slate-700 hover:border-[#101876]/40 hover:text-[#101876]"
                                >
                                  <Eye className="h-3 w-3" /> View
                                </button>
                                <button
                                  onClick={() => setDocLesson(l)}
                                  data-testid={`lesson-add-doc-${l.id}`}
                                  className="inline-flex items-center gap-1 rounded-md bg-[#101876] px-2 py-1 text-[10px] font-semibold text-white hover:bg-[#0c135c]"
                                >
                                  <Plus className="h-3 w-3" /> Add Document
                                </button>
                              </>
                            )}
                            {l.lesson_type === "quiz" && (
                              <>
                                <button
                                  onClick={() => setQuizLinkLesson(l)}
                                  data-testid={`lesson-view-quiz-${l.id}`}
                                  title={l.linked_exam_id ? "Preview linked quiz" : "No quiz linked yet"}
                                  className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-[10px] font-semibold text-slate-700 hover:border-[#101876]/40 hover:text-[#101876]"
                                >
                                  <Eye className="h-3 w-3" /> View
                                </button>
                                <button
                                  onClick={() => setQuizLinkLesson(l)}
                                  data-testid={`lesson-add-quiz-${l.id}`}
                                  className="inline-flex items-center gap-1 rounded-md bg-[#101876] px-2 py-1 text-[10px] font-semibold text-white hover:bg-[#0c135c]"
                                >
                                  <Plus className="h-3 w-3" /> {l.linked_exam_id ? "Change Quiz" : "Add Quiz"}
                                </button>
                              </>
                            )}
                            <div className="text-slate-500">{l.estimated_duration_minutes ? `${l.estimated_duration_minutes} min` : ""}</div>
                            {isSuperAdmin && (
                              <button
                                onClick={() => setEditLesson(l)}
                                data-testid={`lesson-edit-${l.id}`}
                                title="Edit lesson"
                                className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-[10px] font-semibold text-slate-700 hover:border-[#101876]/40 hover:text-[#101876]"
                              >
                                <Pencil className="h-3 w-3" /> Edit
                              </button>
                            )}
                            <button
                              onClick={() => removeLesson(l)}
                              data-testid={`lesson-remove-${l.id}`}
                              title="Remove lesson"
                              className="inline-flex items-center rounded-md border border-transparent p-1 text-slate-400 hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className={`mt-3 rounded border border-dashed ${isDropTarget ? "border-[#329112] bg-emerald-50 text-emerald-700" : "border-slate-300 text-slate-400"} px-3 py-4 text-center text-[11px] font-semibold uppercase tracking-widest`}
                      data-testid={`module-empty-drop-${m.id}`}>
                      {isDropTarget ? "Drop lesson here" : "No lessons yet — add or drop a lesson here"}
                    </div>
                  )}
                </div>
                );
              })}
            </div>

            {/* ============= Linked exams (Phase L1) ============= */}
            <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-3" data-testid="admin-exam-links-panel">
              <div className="text-sm font-bold text-slate-900 flex items-center gap-2">
                Course Exams & Post-Tests
                {examLinks.length > 0 && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-600">{examLinks.length}</span>}
              </div>
              <div className="text-xs text-slate-500">
                Exams linked here appear inside the learner course player. Unlock condition controls when the learner can start them.
              </div>
              {examLinks.length === 0 ? (
                <div className="rounded-md border border-dashed border-slate-300 p-6 text-center text-xs text-slate-500">
                  No exams linked yet. Use <b>Add Exam / Post-Test</b> above to attach a final assessment, post-test, or quiz to this course.
                </div>
              ) : (
                <div className="space-y-2">
                  {examLinks.map((lnk) => (
                    <div key={lnk.id} className="rounded-md border border-slate-200 bg-slate-50/40 p-3" data-testid={`exam-link-${lnk.id}`}>
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-slate-400 font-mono text-xs">{lnk.display_order}.</span>
                            <span className="font-bold text-slate-900 text-sm">{lnk.display_title || lnk.exam?.exam_title}</span>
                            <span className="rounded bg-[#101876]/10 px-1.5 py-0.5 text-[9px] font-bold uppercase text-[#101876]">{(lnk.exam_type || "").replace(/_/g, " ")}</span>
                            <StatusBadge status={lnk.status} />
                          </div>
                          <div className="mt-1 text-xs text-slate-500 flex items-center gap-3 flex-wrap">
                            <span>Exam: <b className="font-semibold text-slate-700">{lnk.exam?.exam_code}</b></span>
                            <span>Duration: {lnk.exam?.duration_minutes} min</span>
                            <span>Questions: {lnk.exam?.total_questions}</span>
                            <span>Pass: {lnk.exam?.passing_percentage}%</span>
                            <span>Max attempts: {lnk.exam?.maximum_attempts}</span>
                          </div>
                          <div className="mt-1 text-[11px] text-slate-500 flex items-center gap-3 flex-wrap">
                            <span>Unlock: <b className="font-semibold text-slate-700">{(lnk.unlock_condition || "").replace(/_/g, " ")}</b></span>
                            {lnk.is_mandatory && <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold text-amber-800">MANDATORY</span>}
                            {lnk.required_for_certificate && <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[9px] font-bold text-emerald-800">CERT REQ.</span>}
                            {lnk.required_for_course_completion && <span className="rounded bg-indigo-100 px-1.5 py-0.5 text-[9px] font-bold text-indigo-800">COMPLETION REQ.</span>}
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <a
                            href={`/app/super-admin/exams/${lnk.exam_id}`}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-semibold text-slate-700 hover:border-[#101876]/40 hover:text-[#101876]"
                            data-testid={`exam-link-view-${lnk.id}`}
                            title="Open exam in a new tab"
                          >
                            <Eye className="h-3 w-3" /> View exam
                          </a>
                          <button
                            onClick={() => {
                              setLinkForm({
                                exam_id: lnk.exam_id, module_id: lnk.module_id || "",
                                display_title: lnk.display_title || "",
                                exam_type: lnk.exam_type, display_order: lnk.display_order,
                                is_mandatory: lnk.is_mandatory,
                                required_for_course_completion: lnk.required_for_course_completion,
                                required_for_certificate: lnk.required_for_certificate,
                                unlock_condition: lnk.unlock_condition,
                                minimum_completion_percentage: lnk.minimum_completion_percentage,
                                specific_module_id: lnk.specific_module_id || "",
                                specific_lesson_id: lnk.specific_lesson_id || "",
                                status: lnk.status,
                              });
                              setLinkDlg(lnk);
                            }}
                            className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-semibold text-slate-600 hover:border-[#101876]/40"
                            data-testid={`exam-link-edit-${lnk.id}`}
                          >Edit</button>
                          {lnk.status === "published" ? (
                            <button onClick={() => toggleLinkStatus(lnk.id, "suspended")} className="rounded-md border border-amber-200 bg-white px-2 py-1 text-xs font-semibold text-amber-700 hover:bg-amber-50" data-testid={`exam-link-suspend-${lnk.id}`}>Suspend</button>
                          ) : (
                            <button onClick={() => toggleLinkStatus(lnk.id, "published")} className="rounded-md border border-emerald-200 bg-white px-2 py-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-50" data-testid={`exam-link-publish-${lnk.id}`}>Publish</button>
                          )}
                          <button onClick={() => deleteExamLink(lnk.id)} className="rounded-md border border-rose-200 bg-white px-2 py-1 text-xs font-semibold text-rose-600 hover:bg-rose-50" data-testid={`exam-link-delete-${lnk.id}`}>Remove</button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="videos">
            <VideoProcessingPanel
              videos={videos}
              loading={videosLoading}
              reprocessing={reprocessing}
              onReprocess={reprocessVideo}
              onDelete={deleteVideo}
              onRefresh={loadVideos}
            />
          </TabsContent>

          <TabsContent value="mapping">
            <div className="rounded-2xl border border-slate-200 bg-white p-6 text-sm">
              Course is currently mapped to <span className="font-bold text-[#101876]">{c.mapped_orgs_count || 0}</span> organization(s).
              <Link to="/app/super-admin/courses/mappings" className="ml-2 inline-flex text-xs font-semibold text-[#101876] hover:text-[#0c135c] underline">Manage mappings</Link>
            </div>
          </TabsContent>

          <TabsContent value="history">
            <div className="rounded-2xl border border-slate-200 bg-white">
              <Table>
                <TableHeader><TableRow>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Old</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">New</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Reason</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">When</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {(payload.status_history || []).length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-xs text-slate-500 py-6">No status changes yet.</TableCell></TableRow>}
                  {payload.status_history.map((h, i) => (
                    <TableRow key={i}>
                      <TableCell className="text-xs"><StatusBadge status={h.old_status} /></TableCell>
                      <TableCell className="text-xs"><StatusBadge status={h.new_status} /></TableCell>
                      <TableCell className="text-xs text-slate-600">{h.reason || "—"}</TableCell>
                      <TableCell className="text-xs text-slate-500">{h.changed_at?.slice(0, 16).replace("T", " ")}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="versions">
            <div className="rounded-2xl border border-slate-200 bg-white">
              <Table>
                <TableHeader><TableRow>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Version</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Notes</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Effective</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Current</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {(payload.versions || []).map((v) => (
                    <TableRow key={v.id}>
                      <TableCell className="font-mono text-xs">v{v.version_number}</TableCell>
                      <TableCell className="text-xs text-slate-600">{v.version_notes || "—"}</TableCell>
                      <TableCell className="text-xs text-slate-500">{v.effective_date?.slice(0, 10)}</TableCell>
                      <TableCell>{v.is_current && <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-700">Current</span>}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={!!statusDlg} onOpenChange={(o) => !o && setStatusDlg(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Change status to {statusDlg?.targetStatus}?</DialogTitle></DialogHeader>
          {statusDlg?.needsReason && (
            <div className="space-y-1"><Label>Reason *</Label><Textarea rows={3} value={statusReason} onChange={(e) => setStatusReason(e.target.value)} data-testid="course-status-reason" /></div>
          )}
          <DialogFooter>
            <button onClick={() => setStatusDlg(null)} className="rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
            <button onClick={submitStatus} className="rounded-md bg-[#101876] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0c135c]" data-testid="course-status-confirm">Confirm</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!modDlg} onOpenChange={(o) => !o && setModDlg(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>{modDlg?.create ? "Add module" : "Edit module"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label>Title *</Label><Input value={modForm.module_title} onChange={(e) => setModForm({ ...modForm, module_title: e.target.value })} data-testid="module-title" /></div>
            <div className="space-y-1"><Label>Description</Label><Textarea rows={2} value={modForm.description} onChange={(e) => setModForm({ ...modForm, description: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Duration (min)</Label><Input type="number" value={modForm.estimated_duration_minutes} onChange={(e) => setModForm({ ...modForm, estimated_duration_minutes: parseInt(e.target.value || "0", 10) })} /></div>
              <div className="space-y-1"><Label>Order</Label><Input type="number" value={modForm.display_order} onChange={(e) => setModForm({ ...modForm, display_order: parseInt(e.target.value || "0", 10) })} /></div>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setModDlg(null)} className="rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
            <button onClick={submitModule} className="rounded-md bg-[#101876] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0c135c]" data-testid="module-submit">{modDlg?.create ? "Add" : "Save"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!lessonDlg} onOpenChange={(o) => !o && setLessonDlg(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Add lesson placeholder</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label>Title *</Label><Input value={lessonForm.lesson_title} onChange={(e) => setLessonForm({ ...lessonForm, lesson_title: e.target.value })} data-testid="lesson-title" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Type</Label>
                <Select value={lessonForm.lesson_type} onValueChange={(v) => setLessonForm({ ...lessonForm, lesson_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{LESSON_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1"><Label>Order</Label><Input type="number" value={lessonForm.display_order} onChange={(e) => setLessonForm({ ...lessonForm, display_order: parseInt(e.target.value || "0", 10) })} /></div>
            </div>
            <div className="text-xs text-slate-500">
              Duration will be auto-filled from the video&apos;s real length when you attach a YouTube URL (or complete the upload).
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setLessonDlg(null)} className="rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
            <button onClick={submitLesson} className="rounded-md bg-[#101876] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0c135c]" data-testid="lesson-submit">Add</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 6 — Add video lesson (device / drive / onedrive / url) */}
      <AddVideoDialog
        open={!!videoLessonId}
        onOpenChange={(o) => !o && setVideoLessonId(null)}
        lessonId={videoLessonId}
        onSuccess={() => load()}
      />

      {/* Super Admin — Edit lesson (title, description, duration, min-watch, replace video) */}
      <EditLessonDialog
        open={!!editLesson}
        onOpenChange={(o) => !o && setEditLesson(null)}
        lesson={editLesson}
        onSuccess={() => { loadModules(); load(); }}
      />

      {/* Admin preview of the uploaded video */}
      <AdminVideoPreviewDialog
        open={!!previewLesson}
        onOpenChange={(o) => !o && setPreviewLesson(null)}
        lesson={previewLesson}
      />

      {/* Attach PDF / documents / handouts to a lesson */}
      <AddDocumentDialog
        open={!!docLesson}
        onOpenChange={(o) => !o && setDocLesson(null)}
        lesson={docLesson}
        onSuccess={loadModules}
      />

      {/* Admin preview of PDF / doc lessons + their resources */}
      <AdminDocumentPreviewDialog
        open={!!previewDocLesson}
        onOpenChange={(o) => !o && setPreviewDocLesson(null)}
        lesson={previewDocLesson}
      />

      {/* Link an existing quiz/exam to a quiz-type lesson */}
      <AdminQuizLinkDialog
        open={!!quizLinkLesson}
        onOpenChange={(o) => !o && setQuizLinkLesson(null)}
        courseId={courseId}
        lesson={quizLinkLesson}
        onSuccess={loadModules}
      />

      {/* Phase L1 — Add/Edit exam link */}
      <Dialog open={!!linkDlg} onOpenChange={(o) => !o && setLinkDlg(null)}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{linkDlg?.create ? "Add Exam / Post-Test" : "Edit Exam Link"}</DialogTitle></DialogHeader>
          <div className="space-y-3" data-testid="exam-link-dialog">
            {linkDlg?.create && (
              <div className="space-y-1">
                <Label>Exam *</Label>
                <select
                  value={linkForm.exam_id}
                  onChange={(e) => {
                    const ex = availableExams.find((x) => x.id === e.target.value);
                    setLinkForm((p) => ({
                      ...p, exam_id: e.target.value,
                      display_title: ex?.exam_title || "",
                    }));
                  }}
                  className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
                  data-testid="exam-link-exam"
                >
                  <option value="">Select an exam</option>
                  {availableExams.filter((e) => !e.already_linked).map((e) => (
                    <option key={e.id} value={e.id}>{e.exam_code} — {e.exam_title} [{e.status}]</option>
                  ))}
                </select>
                {availableExams.filter((e) => !e.already_linked).length === 0 && (
                  <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                    No unlinked exams available for this course. <Link to="/app/super-admin/exams/new" state={{ course_id: courseId }} className="underline font-semibold">Create a new exam</Link> first.
                  </div>
                )}
              </div>
            )}

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label>Display title</Label>
                <Input value={linkForm.display_title} onChange={(e) => setLinkForm((p) => ({ ...p, display_title: e.target.value }))} data-testid="exam-link-title" />
              </div>
              <div className="space-y-1">
                <Label>Exam type</Label>
                <select value={linkForm.exam_type} onChange={(e) => setLinkForm((p) => ({ ...p, exam_type: e.target.value }))} className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" data-testid="exam-link-type">
                  <option value="pre_test">Pre-Test</option>
                  <option value="post_test">Post-Test</option>
                  <option value="final_assessment">Final Assessment</option>
                  <option value="practice_quiz">Practice Quiz</option>
                  <option value="compliance">Compliance Test</option>
                  <option value="competency">Competency Test</option>
                </select>
              </div>
              <div className="space-y-1">
                <Label>Display order</Label>
                <Input type="number" value={linkForm.display_order} onChange={(e) => setLinkForm((p) => ({ ...p, display_order: Number(e.target.value || 0) }))} data-testid="exam-link-order" />
              </div>
              <div className="space-y-1">
                <Label>Module (optional)</Label>
                <select value={linkForm.module_id || ""} onChange={(e) => setLinkForm((p) => ({ ...p, module_id: e.target.value || null }))} className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" data-testid="exam-link-module">
                  <option value="">— none (course-level) —</option>
                  {modules.map((m) => <option key={m.id} value={m.id}>{m.module_title}</option>)}
                </select>
              </div>
            </div>

            <div className="rounded-md border border-slate-200 bg-slate-50/40 p-3 space-y-2">
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Requirements</div>
              <label className="flex items-center gap-2 text-sm"><Switch checked={!!linkForm.is_mandatory} onCheckedChange={(v) => setLinkForm((p) => ({ ...p, is_mandatory: !!v }))} data-testid="exam-link-mandatory" /> Mandatory for learner</label>
              <label className="flex items-center gap-2 text-sm"><Switch checked={!!linkForm.required_for_course_completion} onCheckedChange={(v) => setLinkForm((p) => ({ ...p, required_for_course_completion: !!v }))} data-testid="exam-link-required-completion" /> Required for course completion</label>
              <label className="flex items-center gap-2 text-sm"><Switch checked={!!linkForm.required_for_certificate} onCheckedChange={(v) => setLinkForm((p) => ({ ...p, required_for_certificate: !!v }))} data-testid="exam-link-required-cert" /> Required for certificate</label>
            </div>

            <div className="rounded-md border border-slate-200 bg-slate-50/40 p-3 space-y-2">
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Unlock condition</div>
              <select value={linkForm.unlock_condition} onChange={(e) => setLinkForm((p) => ({ ...p, unlock_condition: e.target.value }))} className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" data-testid="exam-link-unlock">
                <option value="always_available">Always available</option>
                <option value="after_previous_lesson_completion">After previous lesson completion</option>
                <option value="after_all_required_lessons_completion">After all required lessons completion</option>
                <option value="after_course_completion_percentage">After course completion percentage</option>
                <option value="after_specific_module_completion">After specific module completion</option>
                <option value="after_specific_lesson_completion">After specific lesson (mid-course post-test)</option>
              </select>
              {linkForm.unlock_condition === "after_course_completion_percentage" && (
                <div>
                  <Label>Minimum completion %</Label>
                  <Input type="number" min={0} max={100} value={linkForm.minimum_completion_percentage} onChange={(e) => setLinkForm((p) => ({ ...p, minimum_completion_percentage: Number(e.target.value || 0) }))} data-testid="exam-link-min-pct" />
                </div>
              )}
              {linkForm.unlock_condition === "after_specific_module_completion" && (
                <div>
                  <Label>Specific module</Label>
                  <select value={linkForm.specific_module_id || ""} onChange={(e) => setLinkForm((p) => ({ ...p, specific_module_id: e.target.value || null }))} className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" data-testid="exam-link-specific-module">
                    <option value="">Select module</option>
                    {modules.map((m) => <option key={m.id} value={m.id}>{m.module_title}</option>)}
                  </select>
                </div>
              )}
              {linkForm.unlock_condition === "after_specific_lesson_completion" && (
                <div>
                  <Label>Specific lesson</Label>
                  <select
                    value={linkForm.specific_lesson_id || ""}
                    onChange={(e) => setLinkForm((p) => ({ ...p, specific_lesson_id: e.target.value || null }))}
                    className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
                    data-testid="exam-link-specific-lesson"
                  >
                    <option value="">Select lesson</option>
                    {modules.flatMap((m) => (m.lessons || []).map((l) => (
                      <option key={l.id} value={l.id}>{m.module_title} → {l.lesson_title} ({l.lesson_type})</option>
                    )))}
                  </select>
                  <div className="mt-1 text-[10px] text-slate-500">
                    Post-test unlocks the moment this specific lesson (video / PDF / etc.) is marked complete.
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-1">
              <Label>Status</Label>
              <select value={linkForm.status} onChange={(e) => setLinkForm((p) => ({ ...p, status: e.target.value }))} className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" data-testid="exam-link-status">
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setLinkDlg(null)} className="rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
            <button onClick={submitExamLink} className="rounded-md bg-[#101876] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0c135c]" data-testid="exam-link-submit">
              {linkDlg?.create ? "Add exam" : "Save changes"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

function StatCard({ title, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="text-xs font-bold uppercase tracking-widest text-slate-500">{title}</div>
      <div className="mt-1 text-2xl font-black text-slate-900">{value}</div>
    </div>
  );
}

function ParaBlock({ title, text }) {
  return (
    <div>
      <div className="text-xs font-bold uppercase tracking-widest text-slate-500">{title}</div>
      <p className="mt-1 whitespace-pre-line text-sm text-slate-700">{text}</p>
    </div>
  );
}

function fmtBytes(n) {
  if (!n) return "—";
  const kb = n / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb.toFixed(1)} MB`;
  return `${(mb / 1024).toFixed(2)} GB`;
}
function fmtDuration(sec) {
  if (!sec) return "—";
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}
function VideoStatusBadge({ v }) {
  const cs = v.compression_status || "";
  const us = v.upload_status || "";
  let label = cs || us || "unknown";
  let cls = "bg-slate-100 text-slate-600";
  if (us === "ready" && cs === "completed") { label = "READY"; cls = "bg-emerald-100 text-emerald-800"; }
  else if (cs === "processing" || us === "uploaded" || us === "uploading") { label = "PROCESSING"; cls = "bg-amber-100 text-amber-800"; }
  else if (cs === "queued") { label = "QUEUED"; cls = "bg-indigo-100 text-indigo-700"; }
  else if (cs === "failed" || us === "failed") { label = "FAILED"; cls = "bg-rose-100 text-rose-700"; }
  return <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${cls}`}>{label}</span>;
}

function VideoProcessingPanel({ videos, loading, reprocessing, onReprocess, onDelete, onRefresh }) {
  const [subtab, setSubtab] = useState("videos");
  const busy = videos.some((v) => v.compression_status === "processing" || v.upload_status === "uploading" || v.upload_status === "uploaded" || v.compression_status === "queued");
  const failed = videos.filter((v) => v.compression_status === "failed" || v.upload_status === "failed").length;
  return (
    <div className="space-y-4" data-testid="admin-course-videos-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-4">
        <div className="text-sm text-slate-700">
          <span className="font-bold">{videos.length}</span> video{videos.length === 1 ? "" : "s"} in this course
          {failed > 0 && <span className="ml-3 rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-bold text-rose-700">{failed} FAILED</span>}
          {busy && <span className="ml-3 inline-flex items-center gap-1 text-[11px] text-amber-700"><Loader2 className="h-3 w-3 animate-spin" /> processing…</span>}
        </div>
        <button type="button" onClick={onRefresh} data-testid="admin-videos-refresh"
                className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-[#101876]/40">
          <RefreshIcon /> Refresh
        </button>
      </div>

      <Tabs value={subtab} onValueChange={setSubtab}>
        <TabsList data-testid="video-panel-subtabs">
          <TabsTrigger value="videos" data-testid="video-panel-tab-videos">Videos</TabsTrigger>
          <TabsTrigger value="security" data-testid="video-panel-tab-security">Anti-skip settings</TabsTrigger>
        </TabsList>

        <TabsContent value="videos">
          {loading && videos.length === 0 ? (
            <div className="flex items-center gap-2 rounded-xl border border-slate-100 bg-white p-6 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading videos…
            </div>
          ) : videos.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">
              No videos uploaded to this course yet. Add a lesson of type <b>video</b> in the Curriculum tab and click <b>Add Video</b>.
            </div>
          ) : (
            <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
              <table className="w-full text-sm" data-testid="admin-videos-table">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
                  <tr>
                    <th className="p-3 text-left">Lesson / Title</th>
                    <th className="p-3 text-left">Source</th>
                    <th className="p-3 text-center">Status</th>
                    <th className="p-3 text-right">Duration</th>
                    <th className="p-3 text-right">Size</th>
                    <th className="p-3 text-center">HLS</th>
                    <th className="p-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {videos.map((v) => (
                    <tr key={v.id} data-testid={`admin-video-row-${v.id}`}>
                      <td className="p-3">
                        <div className="text-xs font-bold text-slate-800">{v.video_title || v.original_file_name || "Untitled"}</div>
                        <div className="text-[10px] text-slate-500">{v.lesson_title || "—"}</div>
                        {v.processing_error && (
                          <div className="mt-1 text-[10px] text-rose-600" title={v.processing_error}>⚠ {v.processing_error.slice(0, 80)}{v.processing_error.length > 80 ? "…" : ""}</div>
                        )}
                      </td>
                      <td className="p-3 text-[11px] text-slate-500">{(v.source_type || "").replace(/_/g, " ")}</td>
                      <td className="p-3 text-center"><VideoStatusBadge v={v} /></td>
                      <td className="p-3 text-right text-[11px]">{fmtDuration(v.duration_seconds)}</td>
                      <td className="p-3 text-right text-[11px]">{fmtBytes(v.processed_file_size || v.file_size)}</td>
                      <td className="p-3 text-center text-[10px]">
                        {v.has_hls ? <span className="rounded-full bg-emerald-100 px-2 py-0.5 font-bold text-emerald-800">YES</span>
                                  : <span className="rounded-full bg-slate-100 px-2 py-0.5 font-bold text-slate-500">NO</span>}
                      </td>
                      <td className="p-3 text-right">
                        <div className="inline-flex items-center gap-1.5">
                          <button
                            type="button"
                            onClick={() => onReprocess(v.id)}
                            disabled={!!reprocessing[v.id] || v.source_type === "external_url"}
                            data-testid={`admin-video-reprocess-${v.id}`}
                            title={v.source_type === "external_url" ? "External URLs don't require processing." : "Re-run compression + HLS"}
                            className="inline-flex items-center gap-1 rounded-md border border-[#101876] bg-white px-2 py-1 text-[11px] font-bold text-[#101876] hover:bg-[#101876] hover:text-white disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-white disabled:hover:text-[#101876]"
                          >
                            {reprocessing[v.id] ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshIcon />}
                            Re-process
                          </button>
                          <button
                            type="button"
                            onClick={() => onDelete(v)}
                            data-testid={`admin-video-delete-${v.id}`}
                            className="inline-flex items-center rounded-md border border-transparent p-1 text-slate-400 hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600"
                            title="Delete video"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="security">
          <VideoSecurityPanel videos={videos} loading={loading} />
        </TabsContent>
      </Tabs>
    </div>
  );
}


// ============ Anti-skip settings sub-panel ============
// Per-video override editor for the 8 security toggles. Loads the effective
// config + current overrides from the backend, lets the admin flip switches
// (or clear an override back to system default), and persists via PUT.
function VideoSecurityPanel({ videos, loading }) {
  const [selectedId, setSelectedId] = useState(null);
  const [cfg, setCfg] = useState(null); // { effective, defaults, overrides }
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!selectedId && videos.length > 0) setSelectedId(videos[0].id);
  }, [videos, selectedId]);

  const loadCfg = useCallback(async (vid) => {
    if (!vid) return;
    setBusy(true);
    try {
      const { data } = await api.get(`/admin/lesson-videos/${vid}/security`);
      setCfg(data.data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { loadCfg(selectedId); }, [selectedId, loadCfg]);

  const saveField = async (key, value) => {
    if (!selectedId) return;
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/lesson-videos/${selectedId}/security`, { [key]: value });
      setCfg(data.data);
      toast.success("Saved.");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  if (loading && videos.length === 0) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-slate-100 bg-white p-6 text-sm text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading videos…
      </div>
    );
  }
  if (videos.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">
        No videos uploaded to this course yet. Anti-skip settings become configurable once at least one video is added.
      </div>
    );
  }

  const eff = cfg?.effective || {};
  const overrides = cfg?.overrides || {};
  const defaults = cfg?.defaults || {};
  const hasOverride = (k) => Object.prototype.hasOwnProperty.call(overrides, k);
  const selectedVideo = videos.find((v) => v.id === selectedId);

  const TOGGLES = [
    { key: "prevent_fast_forward", label: "Prevent fast-forward", hint: "Blocks learners from seeking past their max watched time." },
    { key: "allow_rewind", label: "Allow rewind", hint: "Learners can jump backwards to any watched point." },
    { key: "allow_playback_speed", label: "Allow playback-speed change", hint: "When off, playback speed is locked at 1.0×." },
    { key: "auto_complete_video", label: "Auto-complete on finish", hint: "Marks the lesson complete once the video reaches the minimum watched %." },
    { key: "steward_logo_watermark_enabled", label: "Steward logo watermark", hint: "Overlays the Steward logo on the player." },
    { key: "dynamic_user_watermark_enabled", label: "Dynamic user watermark", hint: "Overlays the learner's name / email periodically to deter recording." },
  ];

  return (
    <div className="space-y-4" data-testid="admin-video-security-panel">
      <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3">
        <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Select video</div>
        <Select value={selectedId || ""} onValueChange={setSelectedId}>
          <SelectTrigger className="w-full max-w-xl" data-testid="security-video-select">
            <SelectValue placeholder="Choose a video…" />
          </SelectTrigger>
          <SelectContent>
            {videos.map((v) => (
              <SelectItem key={v.id} value={v.id}>
                {v.video_title || v.original_file_name || "Untitled"} — {v.lesson_title || "—"}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {selectedVideo && (
          <div className="text-[11px] text-slate-500">
            Source: <b className="text-slate-700">{(selectedVideo.source_type || "").replace(/_/g, " ")}</b>
            {" · "}Overrides count: <b className="text-slate-700">{Object.keys(overrides).length}</b>
          </div>
        )}
      </div>

      {busy || !cfg ? (
        <div className="flex items-center gap-2 rounded-xl border border-slate-100 bg-white p-6 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading settings…
        </div>
      ) : (
        <div className="rounded-2xl border border-slate-200 bg-white divide-y divide-slate-100">
          {TOGGLES.map((t) => (
            <div key={t.key} className="flex items-start justify-between gap-4 p-4" data-testid={`security-toggle-${t.key}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <div className="text-sm font-bold text-slate-800">{t.label}</div>
                  {hasOverride(t.key) ? (
                    <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[9px] font-bold uppercase text-amber-800">Override</span>
                  ) : (
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[9px] font-bold uppercase text-slate-500">Default: {String(defaults[t.key])}</span>
                  )}
                </div>
                <div className="text-[11px] text-slate-500">{t.hint}</div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <Switch
                  checked={!!eff[t.key]}
                  disabled={saving}
                  onCheckedChange={(val) => saveField(t.key, val)}
                  data-testid={`security-switch-${t.key}`}
                />
                {hasOverride(t.key) && (
                  <button
                    type="button"
                    onClick={() => saveField(t.key, null)}
                    disabled={saving}
                    className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 hover:text-[#101876]"
                    data-testid={`security-reset-${t.key}`}
                    title="Revert to system default"
                  >
                    Reset
                  </button>
                )}
              </div>
            </div>
          ))}

          {/* Numeric thresholds */}
          <div className="grid gap-4 p-4 sm:grid-cols-2" data-testid="security-thresholds">
            <ThresholdField
              label="Minimum watch % (auto-complete)"
              hint="Video must reach this % of duration before the SYSTEM auto-marks the lesson complete."
              defVal={defaults.minimum_watch_percentage}
              curVal={eff.minimum_watch_percentage}
              hasOverride={hasOverride("minimum_watch_percentage")}
              onSave={(v) => saveField("minimum_watch_percentage", v)}
              saving={saving}
              testKey="minimum_watch_percentage"
            />
            <ThresholdField
              label="Manual-complete unlock %"
              hint="Percentage watched at which the learner-facing 'Mark as complete' button unlocks. Must be ≤ minimum watch %."
              defVal={defaults.manual_complete_percentage}
              curVal={eff.manual_complete_percentage}
              hasOverride={hasOverride("manual_complete_percentage")}
              onSave={(v) => saveField("manual_complete_percentage", v)}
              saving={saving}
              testKey="manual_complete_percentage"
            />
          </div>
        </div>
      )}
    </div>
  );
}

function ThresholdField({ label, hint, defVal, curVal, hasOverride, onSave, saving, testKey }) {
  const [val, setVal] = useState(curVal ?? defVal ?? "");
  useEffect(() => { setVal(curVal ?? defVal ?? ""); }, [curVal, defVal]);
  const commit = () => {
    const n = parseInt(val, 10);
    if (Number.isNaN(n) || n < 1 || n > 100) { toast.error("Value must be between 1 and 100."); return; }
    if (n === curVal) return;
    onSave(n);
  };
  return (
    <div>
      <div className="flex items-center gap-2">
        <Label className="text-sm font-bold text-slate-800">{label}</Label>
        {hasOverride ? (
          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[9px] font-bold uppercase text-amber-800">Override</span>
        ) : (
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[9px] font-bold uppercase text-slate-500">Default: {defVal}%</span>
        )}
      </div>
      <div className="mt-1 text-[11px] text-slate-500">{hint}</div>
      <div className="mt-2 flex items-center gap-2">
        <Input
          type="number" min={1} max={100}
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onBlur={commit}
          disabled={saving}
          className="w-28"
          data-testid={`security-input-${testKey}`}
        />
        <span className="text-xs text-slate-500">%</span>
        {hasOverride && (
          <button
            type="button"
            onClick={() => onSave(null)}
            disabled={saving}
            className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 hover:text-[#101876]"
            data-testid={`security-reset-${testKey}`}
          >
            Reset
          </button>
        )}
      </div>
    </div>
  );
}

function RefreshIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-3 w-3">
      <path d="M23 4v6h-6" /><path d="M1 20v-6h6" />
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
    </svg>
  );
}
